/**
 * ChargePaymentModal.tsx
 *
 * Full payment charge flow matching legacy payment-details-charge-gateway.php.
 * Steps: merchant selection, payment source, type/amount, gift cards, credits,
 * review, then two-phase save-and-charge through Forward.
 */
import { Fragment, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Gift, AlertTriangle } from 'lucide-react';
import {
  createPatientPayment,
  chargeForwardPayment,
  listDiscounts,
  listQuoteItems,
  listPaymentMethodOptions,
  markTerminalTransactionComplete,
  type BillingMethod,
  type Merchant,
  type ForwardPaymentIntent,
  type PaymentTerminal,
  type PaymentDiscount,
  type QuoteItem,
  type TerminalCheckoutResult,
  type TerminalTokenizeResult,
} from '@/services/payments.service';
import { getErrorMessage } from '@/services/apiClient';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { todayInTz } from '@/lib/facilityTimezone';
import { MerchantSelector } from '@/components/payments/MerchantSelector';
import {
  PaymentSourceSelector,
  parseTerminalToken,
} from '@/components/payments/PaymentSourceSelector';
import { ForwardBillingMethodCapture } from '@/components/payments/ForwardBillingMethodCapture';
import { TerminalCheckoutPanel, type TerminalFlowResult } from '@/components/payments/TerminalCheckoutPanel';
import {
  GiftCardSelector,
  type AppliedGiftCard,
} from '@/components/payments/GiftCardSelector';
import {
  PaymentProductsSection,
  computeLineTotal,
  type ProductLine,
} from '@/components/payments/PaymentProductsSection';
import { PaymentShippingSection } from '@/components/payments/PaymentShippingSection';
import { useShippingFees } from '@/components/payments/useShippingFees';
import { getPatient } from '@/services/patients.service';
import { appointmentsService } from '@/services/appointments.service';
import { useGlobalFacilityStore } from '@/store/globalFacilityStore';
import { RecordPatientPaymentModal } from './RecordPatientPaymentModal';

const EMPTY_DISCOUNTS: PaymentDiscount[] = [];
const EMPTY_QUOTE_ITEMS: QuoteItem[] = [];

const PAY_TYPE_OPTIONS = [
  'Payment',
  'Deposit',
  'Consult Fee',
  'Payment Adjustment',
  'Discount',
  'Refund',
];

/** Sentinel thrown from the charge mutation to reach the declined banner. */
class DeclinedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DeclinedError';
  }
}

type Step = 'form' | 'review' | 'processing' | 'result';

/**
 * One line item supplied by an upstream caller (currently the appointment
 * modal's "Required Products" panel). Mirrors the seed data used by legacy
 * `loadApptPaymentProducts` in `payments-test.js`, which emits a single
 * `productID,quantity,rate` triple per product type with `quantity` summed
 * across all pending slots for that type.
 */
export interface ChargePaymentPrefillItem {
  quoteItemCostId: number;
  /** Sum of pending slots for this product type. */
  quantity: number;
  /** Optional fallback price when the catalog lookup doesn't find the item. */
  unitPrice?: number;
  productTypeName?: string;
  /**
   * One entry per pending SAP slot in this group. The backend creates a
   * single `payment_materials` row for the group and then links each SAP id
   * back to that material so every slot reads as paid.
   */
  scheduledAppointmentProductsIds?: number[];
}

interface ChargePaymentModalProps {
  patientId: number;
  patientName?: string;
  open: boolean;
  onClose: () => void;
  /** Receives the id of the payment just created, so callers can attach it. */
  onSaved: (paymentId?: number) => void;
  /**
   * When the payment is for a scheduled appointment, pass its id so the
   * backend can audit the linkage and update SAP rows after the charge.
   */
  appointmentId?: number;
  /**
   * Optional pre-seed for the line-items section. When provided, these rows
   * are pushed into `productLines` on open and the amount is auto-populated.
   * Empty array = same as omitting the prop.
   */
  prefillItems?: ChargePaymentPrefillItem[];
  /** Facility from the appointment context; auto-selects the matching merchant. */
  facilityId?: number | null;
  /** When true, the prefilled prices reflect membership pricing. */
  isMember?: boolean;
  /**
   * The plan this payment is for, saved as payments.planID. Opened from a
   * plan's Payments card; omitted everywhere else, which saves no plan.
   */
  planId?: number;
}

export function ChargePaymentModal({
  patientId,
  patientName,
  open,
  onClose,
  onSaved,
  appointmentId,
  prefillItems,
  facilityId: externalFacilityId,
  isMember,
  planId: externalPlanId,
}: ChargePaymentModalProps) {
  const queryClient = useQueryClient();
  const userTz = useUserTimezone();
  const globalFacilityId = useGlobalFacilityStore((s) => s.selectedFacilityIds[0] ?? null);

  const { data: patientData } = useQuery({
    queryKey: ['patient', patientId],
    queryFn: () => getPatient(patientId),
    enabled: open && !patientName,
    staleTime: 5 * 60_000,
  });

  const { data: catalogueData } = useQuery({
    queryKey: ['payment-member-check', patientId],
    queryFn: () => appointmentsService.getAppointmentProductCatalogue(null, patientId),
    enabled: open && !!patientId,
    staleTime: 2 * 60_000,
  });
  const resolvedIsMember = catalogueData?.isMember ?? isMember ?? false;
  const memberStatus: string | null = catalogueData?.memberStatus ?? null;
  const showAsMember = resolvedIsMember || memberStatus === 'frozen' || memberStatus === 'declined';

  const { data: discounts = EMPTY_DISCOUNTS } = useQuery({
    queryKey: ['payment-discounts'],
    queryFn: () => listDiscounts(),
    staleTime: 5 * 60_000,
  });

  const hasPrefill = (prefillItems?.length ?? 0) > 0;
  const { data: quoteItems = EMPTY_QUOTE_ITEMS } = useQuery({
    queryKey: ['payment-quote-items-all'],
    queryFn: () => listQuoteItems({}),
    enabled: open && hasPrefill,
    staleTime: 5 * 60_000,
  });

  const resolvedPatientName = patientName
    || (patientData ? [patientData.firstName, patientData.lastName].filter(Boolean).join(' ') : null);

  const [step, setStep] = useState<Step>('form');
  const [error, setError] = useState<string | null>(null);

  const [merchantId, setMerchantId] = useState<number | null>(null);
  const [merchant, setMerchant] = useState<Merchant | null>(null);
  const [paymentToken, setPaymentToken] = useState('new');
  const [selectedMethod, setSelectedMethod] = useState<BillingMethod | null>(null);
  const [planId, setPlanId] = useState('');
  const [quoteId, setQuoteId] = useState('');
  const [facilityId, setFacilityId] = useState('');

  const [payType, setPayType] = useState('Payment');
  const [amount, setAmount] = useState('');
  const [memo, setMemo] = useState('');

  const [giftCards, setGiftCards] = useState<AppliedGiftCard[]>([]);

  const [productLines, setProductLines] = useState<ProductLine[]>([]);
  const [amountFromProducts, setAmountFromProducts] = useState(false);

  // Terminal (in-person) state
  const [selectedTerminal, setSelectedTerminal] = useState<PaymentTerminal | null>(null);
  const [terminalResult, setTerminalResult] = useState<TerminalFlowResult | null>(null);

  const [intent, setIntent] = useState<ForwardPaymentIntent | null>(null);
  const [chargeResult, setChargeResult] = useState<'success' | 'declined' | null>(null);
  const [declineMessage, setDeclineMessage] = useState<string | null>(null);
  // Payment created in Phase 1. Set as soon as the row exists (before the
  // gateway call) so the result step can link to it on a decline too — the
  // payment row is still there for staff to inspect or retry.
  const [savedPaymentId, setSavedPaymentId] = useState<number | null>(null);
  // Which follow-up modal the result step handed off to. Set means this modal
  // renders the detail modal in its place ('receipt' opens it with the receipt
  // panel already showing); the charge result is done being useful by then.
  const [followUp, setFollowUp] = useState<null | 'details' | 'receipt'>(null);
  // Surfaced on the `result` step when the terminal charge succeeded but
  // the saved-card row could not be persisted (e.g. DB connection blip).
  // Auto-submit (Fix 13) unmounts TerminalCheckoutPanel before staff would
  // normally see the panel's amber banner, so we lift the warning here.
  const [saveWarning, setSaveWarning] = useState<string | null>(null);

  const prefillProductLines: ProductLine[] = useMemo(() => {
    if (!prefillItems || prefillItems.length === 0) return [];
    if (quoteItems.length === 0) return [];

    const memberDisc = discounts.find((d) => d.discountType === 4);
    const memberDiscId = memberDisc?.paymentDiscountID ?? null;

    return prefillItems.map((item) => {
      const qi = quoteItems.find((q) => q.itemId === item.quoteItemCostId);
      // Prefer an explicit unitPrice when provided (integration orders carry the
      // server-computed amount here; the catalog entry may have a stale $0 price).
      const retailPrice = (item.unitPrice != null && item.unitPrice > 0)
        ? item.unitPrice
        : (qi?.price ?? 0);
      // Prefer the explicit per-item name (integration orders pass the real
      // product name here — peptide, pen, needles — even though every line shares
      // one catalog item). Falls back to the catalog name for other callers.
      const catalogName = item.productTypeName ?? qi?.name ?? `Product #${item.quoteItemCostId}`;
      const taxable = qi?.taxable ?? false;
      const productType = qi?.productType ?? 0;

      const memberPrice = qi?.memberPrice ?? 0;
      const hasMemberPrice = showAsMember && memberPrice > 0;
      const memberSavingsPerUnit = hasMemberPrice ? retailPrice - memberPrice : 0;
      const applyMemberDisc = resolvedIsMember && hasMemberPrice && memberSavingsPerUnit > 0 && memberDiscId != null;

      return {
        itemId: item.quoteItemCostId,
        name: catalogName,
        description: qi?.description ?? '',
        unit: qi?.unit ?? '',
        price: retailPrice,
        quantity: item.quantity,
        taxable,
        salesTax: taxable ? 0 : 0,
        discount1Id: applyMemberDisc ? memberDiscId : null,
        manualDiscount1: applyMemberDisc ? memberSavingsPerUnit * item.quantity : 0,
        discount2Id: null,
        manualDiscount2: 0,
        productType,
        scheduledAppointmentProductsId: item.scheduledAppointmentProductsIds?.[0],
        scheduledAppointmentProductsIds: item.scheduledAppointmentProductsIds,
        priceLocked: true,
        retailPrice: hasMemberPrice ? retailPrice : null,
        memberSavingsPerUnit,
        allowPriceOverride: qi?.allowPriceOverride ?? false,
        // Appointment-prefilled lines carry their shipping fee too, so checking
        // out an appointment charges shipping the same as adding the product by
        // hand would.
        shippingFee: qi?.shippingFee ?? null,
      };
    });
  }, [prefillItems, resolvedIsMember, showAsMember, discounts, quoteItems]);

  useEffect(() => {
    if (!open) return;
    setStep('form');
    setError(null);
    setPaymentToken('new');
    setSelectedMethod(null);
    setSelectedTerminal(null);
    setTerminalResult(null);
    // Reset to the caller's plan, not to blank: this runs on every open, and a
    // blank here is what saved every V5 charge on plan 0.
    setPlanId(externalPlanId ? String(externalPlanId) : '');
    setQuoteId('');
    setPayType('Payment');
    setMemo('');
    setGiftCards([]);
    setProductLines(prefillProductLines);
    setAmountFromProducts(prefillProductLines.length > 0);
    setAmount(prefillProductLines.length > 0
      ? prefillProductLines.reduce((sum, line) => sum + computeLineTotal(line, discounts), 0).toFixed(2)
      : '');
    setIntent(null);
    setChargeResult(null);
    setDeclineMessage(null);
    setSaveWarning(null);
    setSavedPaymentId(null);
    setFollowUp(null);
    // Re-arm the terminal auto-submit guard for the new modal session.
    // Today this is implicit (a fresh terminalResult is always a new object
    // identity) but pinning it explicitly here is cheap and removes the
    // implicit coupling between Fix 13's effect and React's render cycle.
    autoSubmittedRef.current = null;

    // Pick merchant from cached data so the selection happens in a single
    // setState batch — avoids the parent/child effect race where the child
    // auto-selects and the parent immediately clears it back to null.
    // When no facility context exists (no externalFacilityId and no global
    // facility selected), leave the merchant blank so the user is forced to
    // choose — prevents accidentally charging to the wrong merchant.
    const cached = queryClient.getQueryData<Merchant[]>(['payment-merchants']) ?? [];
    const effectiveFacId = externalFacilityId ?? globalFacilityId;
    const facMatch = effectiveFacId != null
      ? cached.find((m) => m.facilityId === effectiveFacId)
      : null;
    // A single merchant is unambiguous, so pre-select it even without facility
    // context (mirrors MerchantSelector's auto-select).
    const pick = facMatch ?? (cached.length === 1
      ? cached[0]
      : (effectiveFacId != null
        ? (cached.find((m) => m.isDefault) ?? cached[0] ?? null)
        : null));
    setMerchantId(pick?.merchantId ?? null);
    setMerchant(pick);
    setFacilityId(pick?.facilityId ? String(pick.facilityId) : '');
  }, [open, prefillProductLines, discounts, externalFacilityId, externalPlanId, queryClient]);

  const parsedAmount = useMemo(() => {
    const n = Number(amount);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }, [amount]);

  const giftCardTotal = useMemo(
    () => giftCards.reduce((sum, c) => sum + c.amount, 0),
    [giftCards],
  );

  // ─── Shipping: once per distinct fee, added on top of the product base ─────
  // Phase 1 (createPatientPayment) posts the PRE-shipping base plus the fee IDs;
  // the server resolves the dollars from `shipping_fees` and folds them into
  // curAmount. Phase 2 (the gateway capture) posts the SHIPPING-INCLUSIVE base,
  // because the server no longer re-adds it there — see ForwardChargeService.
  const shipping = useShippingFees({
    productLines,
    resetKey: open,
    enabled: open,
  });

  // Mirrors the server's order exactly: products + shipping, then gift cards,
  // then clamp at zero. Clamping before shipping (as netChargeAmount does) would
  // disagree with the server whenever a gift card exceeds the product subtotal.
  const chargeBaseAmount = useMemo(
    () =>
      Math.max(
        0,
        Math.round((parsedAmount + shipping.shippingTotal - giftCardTotal) * 100) / 100,
      ),
    [parsedAmount, shipping.shippingTotal, giftCardTotal],
  );

  // Payment-method options carry the configured surcharge so we can disclose it
  // before the charge. Display only — the backend re-computes and folds the
  // surcharge authoritatively at charge time, and we still send the base amount.
  const { data: methodOptions } = useQuery({
    queryKey: ['payment-method-options'],
    queryFn: listPaymentMethodOptions,
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const surchargeAmount = useMemo(() => {
    const pmId = selectedMethod?.paymentMethodID;
    if (!pmId || chargeBaseAmount <= 0) return 0;
    const opt = (methodOptions ?? []).find((o) => o.paymentMethodID === pmId);
    if (!opt || opt.surchargeAmount == null || opt.surchargeAmount <= 0) return 0;
    // Mirror the backend rule: percent = round(base * rate / 100, 2); flat = rate.
    // Base is shipping-inclusive, matching ForwardChargeService.
    return opt.surchargeType === 'percent'
      ? Math.round(chargeBaseAmount * opt.surchargeAmount) / 100
      : opt.surchargeAmount;
  }, [selectedMethod, methodOptions, chargeBaseAmount]);

  const totalToCharge = useMemo(
    () => Math.round((chargeBaseAmount + surchargeAmount) * 100) / 100,
    [chargeBaseAmount, surchargeAmount],
  );

  const handleMerchantChange = useCallback((id: number, m: Merchant) => {
    setMerchantId(id);
    setMerchant(m);
    setTerminalResult(null);
    setSaveWarning(null);
    if (m.facilityId) {
      setFacilityId(String(m.facilityId));
    }
  }, []);

  const handleSourceChange = useCallback(
    (token: string, method: BillingMethod | null, terminal: PaymentTerminal | null = null) => {
      setPaymentToken(token);
      setSelectedMethod(method);
      setSelectedTerminal(terminal);
      setTerminalResult(null);
      setSaveWarning(null);
    },
    [],
  );

  const handleTerminalResult = useCallback((result: TerminalFlowResult) => {
    setTerminalResult(result);
    // Lift save_warning into the modal so it survives the panel unmount that
    // happens when we auto-advance to processing/result (Fix 13). Without
    // this, staff would never see "card was charged but could not be saved".
    setSaveWarning(result.data.save_warning ?? null);
    // Checkout mode already captured on the terminal; fast-path to review so
    // the user can confirm and save without a second round-trip.
    if (result.mode === 'checkout') {
      setStep('review');
    }
  }, []);

  const handleTerminalReset = useCallback(() => {
    setTerminalResult(null);
    setSaveWarning(null);
  }, []);


  const handleNewSourceSaved = useCallback((method: BillingMethod) => {
    setPaymentToken(method.token);
    setSelectedMethod(method);
    // Symmetric with handleSourceChange / handleMerchantChange (Fix P) —
    // saving a new card via the new-card form is a context switch away
    // from any prior terminal attempt. Clearing here prevents a stale
    // terminal `save_warning` from rendering on the final result step.
    setTerminalResult(null);
    setSaveWarning(null);
  }, []);

  /**
   * Products total. When the user has added line items, the Amount field
   * becomes derived (matches legacy `updatePaymentTotals` which disables
   * manual amount entry while products are present).
   */
  const productsTotal = useMemo(
    () => productLines.reduce((sum, line) => sum + computeLineTotal(line, discounts), 0),
    [productLines, discounts],
  );

  useEffect(() => {
    if (productLines.length > 0) {
      const value = productsTotal.toFixed(2);
      setAmount(value);
      setAmountFromProducts(true);
    } else if (amountFromProducts) {
      setAmount('');
      setAmountFromProducts(false);
    }
  }, [productsTotal, productLines.length, amountFromProducts]);

  const terminalTokenId = parseTerminalToken(paymentToken);
  const isTerminalMode = terminalTokenId !== null;

  function handleReview() {
    setError(null);
    if (productLines.length === 0) { setError('Add at least one product before charging.'); return; }
    if (parsedAmount < 0) { setError('Enter a valid amount.'); return; }
    // A merchant (gateway) is only involved when actual card money moves.
    // Gift cards / credits covering the full amount need no merchant — an
    // account with no gateway configured can still take those payments.
    // Gated on the shipping-inclusive base: a payment whose products are fully
    // covered by a gift card can still owe shipping, and that has to be charged.
    if (chargeBaseAmount > 0 && !merchantId) { setError('Select a merchant account.'); return; }
    if (chargeBaseAmount > 0) {
      if (isTerminalMode) {
        if (!terminalResult) {
          setError('Run the terminal charge or card read before reviewing.');
          return;
        }
      } else if (!selectedMethod) {
        setError('Select a saved payment source or complete the new card entry first.');
        return;
      }
    }
    setStep('review');
  }

  const chargeMutation = useMutation({
    mutationFn: async () => {
      setStep('processing');
      setDeclineMessage(null);
      // Clear stale form-validation errors set by handleReview (e.g.
      // "Run the terminal charge or card read before reviewing.") that
      // were never cleared because the Fix 13 auto-submit useEffect
      // bypasses handleReview's setError(null). Without this, the red
      // banner from a prior aborted Review attempt leaks onto the
      // success / result step alongside the green confirmation.
      setError(null);

      // Terminal-capture path keeps legacy parity with the in-person reader:
      // the device already authorized and captured the card, so we only run
      // Phase 1 (persist the payment) using the real gateway tx/status. For
      // every other path (web token, terminal tokenize-only, no-charge),
      // Phase 1 saves with void=0 + deferred trigger and Phase 2 calls
      // chargeForwardPayment which mirrors legacy payment_process_charge_authnet.php.
      let terminalMethodTitle: string | null = null;
      let terminalTxId: string | null = null;
      let terminalPiId: string | null = null;
      let terminalStatus: string | null = null;
      let terminalRef: string | null = null;
      let paymentMethodToken: string | null = selectedMethod?.token ?? null;
      let isTerminalCheckout = false;

      if (isTerminalMode && terminalResult) {
        if (terminalResult.mode === 'checkout') {
          const data: TerminalCheckoutResult = terminalResult.data;
          // Only ever send the real Forward payment id (pmt_*) as the
          // transaction id. Never fall back to the payment_intent id (pi_*) —
          // that would land in payment_transactions_authnet.transactionID,
          // which must hold pmt_*. The pi_* is carried separately in
          // terminalPiId → paymentIntentId → the intent bridge. The backend
          // (TerminalForwardService::checkout) resolves the real pmt_* when
          // auth-card omits it, so transaction_id should be populated here.
          terminalTxId = data.transaction_id ?? null;
          // Forward payment_intent id (pi_*) kept distinct from the
          // payment id (pmt_*) so the backend can write the
          // payment_transactions_intents bridge. Legacy parity:
          // payment_process_charge_authnet.php:47-48 reads
          // `terminal_payment_intent_id` as a separate field and
          // writes the bridge at :479-488.
          terminalPiId = data.payment_intent_id ?? null;
          terminalStatus = data.status;
          terminalRef = data.transaction_id ?? data.payment_intent_id;
          paymentMethodToken = data.token ?? paymentMethodToken;
          terminalMethodTitle = data.card_brand
            ? `Terminal ${data.card_brand}${data.masked_pan ? ' ****' + data.masked_pan : ''}`
            : 'Terminal';
          isTerminalCheckout = true;
        } else {
          const data: TerminalTokenizeResult = terminalResult.data;
          paymentMethodToken = data.token ?? paymentMethodToken;
          terminalMethodTitle = data.card_brand
            ? `Terminal ${data.card_brand}${data.masked_pan ? ' ****' + data.masked_pan : ''}`
            : 'Terminal';
        }
      }

      const items = productLines.map((line) => ({
        paymentMaterialID: line.paymentMaterialID,
        itemId: line.itemId,
        // Integration checkout lines (Axiom, GetLabs, …) all share one generic
        // catalog item per module — without the real name the server has no
        // way to tell which peptide/pen/kit this line was, and falls back to
        // the catalog's generic name for every line.
        name: line.name || undefined,
        description: line.description || undefined,
        price: line.price,
        quantity: line.quantity,
        taxable: line.taxable,
        salesTax: line.salesTax,
        discount1Id: line.discount1Id ?? undefined,
        discount2Id: line.discount2Id ?? undefined,
        manualDiscount1: line.manualDiscount1,
        manualDiscount2: line.manualDiscount2,
        scheduledAppointmentProductsId: line.scheduledAppointmentProductsId,
        scheduledAppointmentProductsIds: line.scheduledAppointmentProductsIds,
        // Gift-card SKU fields. When productType=1 the server issues a
        // new gift_cards row + gift_cards_products linkage. Top-up of an
        // existing card (giftCardId / giftCardTitle) is not exposed in
        // the current UI; the API accepts those fields when wired later.
        productType: line.productType,
        creditValue: line.creditValue,
        serialNumber: line.serialNumber || undefined,
        targetGiftCardPatientId: line.targetGiftCardPatientId,
      }));

      // Phase 1: persist the payment + related legacy ledger (materials,
      // gift cards, loans, credits). Mirrors legacy savePaymentForCharge.
      //
      // deferPaymentTrigger keeps the payment-trigger from firing here when
      // we still need to hit the gateway — legacy only runs TaskProcessor
      // AFTER the capture succeeds, so we mirror that in Phase 2.
      // Shipping-inclusive: products fully covered by a gift card can still leave
      // shipping to collect, and skipping the gateway there would record a
      // curAmount that was never actually charged.
      const willRunGatewayCharge = !isTerminalCheckout && chargeBaseAmount > 0;
      const payment = await createPatientPayment(patientId, {
        amount: parsedAmount,
        // Stored verbatim as payments.dtmDate — must be today in the user's
        // zone, not the UTC day (see todayInTz).
        date: todayInTz(userTz),
        method: terminalMethodTitle ?? selectedMethod?.title ?? 'Forward',
        strPayType: payType,
        memo: memo.trim() || undefined,
        referenceNumber: terminalRef ?? undefined,
        paymentMethodID: selectedMethod?.paymentMethodID ?? undefined,
        paymentMethodToken: paymentMethodToken ?? undefined,
        accountAuthnetID: merchantId ?? undefined,
        planID: Number(planId) || undefined,
        quoteID: Number(quoteId) || undefined,
        facilityID: Number(facilityId) || undefined,
        // Only stamp gateway fields during Phase 1 when the terminal already
        // captured; otherwise Phase 2 writes them onto payment_transactions_authnet.
        // Shipping-inclusive: the terminal already captured this exact figure.
        chargeAmount: isTerminalCheckout && chargeBaseAmount > 0 ? chargeBaseAmount : undefined,
        gatewayTransactionId: isTerminalCheckout ? (terminalTxId ?? undefined) : undefined,
        gatewayStatus: isTerminalCheckout ? (terminalStatus ?? undefined) : undefined,
        paymentIntentId: isTerminalCheckout ? (terminalPiId ?? undefined) : undefined,
        giftCards: giftCards
          .map((card) => ({
            giftCardId: card.giftCard.giftCardId ?? card.giftCard.paymentsCreditID,
            amount: card.amount,
          }))
          .filter((card) => Number.isFinite(card.giftCardId) && card.giftCardId > 0 && card.amount > 0),
        // IDs only — the server owns the dollar amounts. See useShippingFees.
        // Always sent, including as an empty array: the server reads an ABSENT
        // key as "caller has no opinion" and preserves existing rows, so an
        // omitted-when-empty payload would make removing the last fee a no-op.
        shipping: shipping.payload,
        items: items.length > 0 ? items : undefined,
        appointmentId: appointmentId,
        deferPaymentTrigger: willRunGatewayCharge || undefined,
      });

      // Record the id before Phase 2 so the "View Payment Details" action on
      // the result step works on the declined path as well (the DeclinedError
      // thrown below skips the mutation's return value entirely).
      setSavedPaymentId(payment.paymentID);

      // Mirrors legacy payments-test.js:2639-2642 — fire-and-forget mark-complete
      // on the terminal_transaction_log row so the patient-detail reconciler
      // skips it. If this never fires (network drop after createPatientPayment
      // succeeds), the reconciler will detect the row at <5min, leave it alone
      // until grace expires, then see the matching payment_transactions_authnet
      // row by transactionID and mark complete idempotently — no double-void.
      if (isTerminalCheckout && terminalResult?.mode === 'checkout') {
        const logId = terminalResult.data.transaction_log_id;
        if (typeof logId === 'number' && logId > 0) {
          void markTerminalTransactionComplete(logId, payment.paymentID).catch((err) => {
            console.warn('[ChargePaymentModal] mark-complete failed', err);
          });
        }
      }

      // Phase 2: charge through Forward. Mirrors legacy
      // confirmPaymentChargeWithToken -> /payment_process_charge_authnet.php.
      // The server atomically handles capture, transaction-log inserts,
      // merchant-default upsert, and void-on-decline (with credit/gift-card
      // reversal). We treat `success=false` like the legacy JS alert and
      // route to the red "Transaction error: Payment was declined." banner.
      let chargeIntentId: string | null = null;
      if (willRunGatewayCharge && paymentMethodToken && merchantId) {
        const charge = await chargeForwardPayment(payment.paymentID, {
          merchant_id: merchantId,
          payment_method_id: paymentMethodToken,
          amount: chargeBaseAmount,
          patient_id: patientId,
        });

        chargeIntentId = charge.intentId || null;
        if (charge.intentId) {
          setIntent({
            client_secret: null,
            id: charge.intentId,
            amount: chargeBaseAmount,
            status: charge.status,
            public_api_key: null,
            merchant_id: merchantId,
            forward_gateway_account_id: null,
          });
        }

        if (!charge.success) {
          throw new DeclinedError(
            charge.message || 'Transaction error: Payment was declined.',
          );
        }
      }

      return {
        paymentId: payment.paymentID,
        status: 'success' as const,
        intentId: chargeIntentId ?? terminalTxId ?? undefined,
      };
    },
    onSuccess: (result) => {
      setChargeResult(result.status);
      setStep('result');
      onSaved(result.paymentId);
      void queryClient.invalidateQueries({ queryKey: ['patient-payments', patientId] });
    },
    onError: (err: unknown) => {
      if (err instanceof DeclinedError) {
        setChargeResult('declined');
        setDeclineMessage(err.message);
        setStep('result');
        return;
      }
      const msg = getErrorMessage(err, 'Payment processing failed. Please try again.');
      console.error('[ChargePaymentModal] Charge failed:', err);
      setError(msg);
      setStep('form');
    },
  });

  // Mirrors legacy parity: payments-test.js auto-fires
  // payment_process_charge_authnet.php immediately after a successful
  // terminal checkout, with no extra click. The user already entered
  // amount / products / notes before pressing Start Terminal Charge, so
  // there's nothing left to confirm — making them click "Save Payment"
  // a second time is the abandonment trap that leaves orphan Forward
  // charges. The ref guard ensures we only auto-fire once per terminal charge.
  //
  // Keyed on `transaction_log_id` — the server-side identity of the charge —
  // rather than on the result object. Object identity was too weak: Reset then
  // recharge produces a NEW result object for what may be the same underlying
  // Forward charge, and the guard would let a second save auto-fire with no
  // click. A missing log id falls back to the payment intent id, and only if
  // both are absent do we fall back to object identity.
  const autoSubmittedRef = useRef<unknown>(null);
  useEffect(() => {
    if (terminalResult?.mode !== 'checkout') return;

    // Falls back to the result object itself when neither id is present, which
    // is the old behaviour — never to a null/constant key, which would make the
    // guard match on every run and auto-fire repeatedly.
    const logId = terminalResult.data.transaction_log_id;
    const intentId = terminalResult.data.payment_intent_id;
    const chargeKey: unknown =
      typeof logId === 'number' && logId > 0
        ? `log:${logId}`
        : intentId
          ? `pi:${intentId}`
          : terminalResult;

    if (step === 'review' && !chargeMutation.isPending && autoSubmittedRef.current !== chargeKey) {
      autoSubmittedRef.current = chargeKey;
      chargeMutation.mutate();
    }
  }, [terminalResult, step, chargeMutation]);

  const missingMemberDiscount = showAsMember && discounts.length > 0 && !discounts.some((d) => d.discountType === 4);

  if (!open) return null;

  // Hand off to the payment detail modal, replacing this one rather than
  // stacking on top of it — the charge result has nothing left to offer once
  // staff have chosen an action, and two overlapping dialogs read as clutter.
  // We stay mounted (so mutation state survives) and simply render the detail
  // modal in place of ours; closing it dismisses the whole flow via onClose.
  if (followUp && savedPaymentId) {
    return (
      <RecordPatientPaymentModal
        open
        patientId={patientId}
        patientName={resolvedPatientName ?? undefined}
        paymentId={savedPaymentId}
        initialPanel={followUp === 'receipt' ? 'receipt' : undefined}
        onClose={() => {
          setFollowUp(null);
          onClose();
        }}
        onSaved={() => {
          onSaved();
          void queryClient.invalidateQueries({ queryKey: ['patient-payments', patientId] });
          void queryClient.invalidateQueries({ queryKey: ['payment-detail', savedPaymentId] });
        }}
      />
    );
  }

  const isPending = chargeMutation.isPending;

  return (
    <Modal
      isOpen={open}
      onClose={isPending ? () => {} : onClose}
      title={
        <div>
          <h2 className="text-base font-semibold text-slate-900">Charge Payment</h2>
          <p className="text-sm font-normal text-slate-500">
            {resolvedPatientName || `Patient #${patientId}`}
            {showAsMember && (
              <span className="ml-2 inline-flex items-center rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-semibold text-emerald-700">Member</span>
            )}
            {memberStatus === 'frozen' && (
              <span className="ml-1 inline-flex items-center rounded bg-blue-100 px-1.5 py-0.5 text-xs font-semibold text-blue-700">Frozen</span>
            )}
            {memberStatus === 'declined' && (
              <span className="ml-1 inline-flex items-center rounded bg-red-100 px-1.5 py-0.5 text-xs font-semibold text-red-700">Past Due</span>
            )}
          </p>
        </div>
      }
      size="xxl"
      footer={
        step === 'form' ? (
          <div className="flex w-full items-center justify-end gap-4">
            {giftCardTotal > 0 ? (
              <div className="flex items-center gap-3 text-sm">
                {/* Struck-through figure must be the shipping-INCLUSIVE gross, or
                    the strip reads "$100 − $40 = $85" and doesn't add up. */}
                <span className="text-slate-400 line-through">
                  ${(parsedAmount + shipping.shippingTotal).toFixed(2)}
                </span>
                <span className="flex items-center gap-1 text-xs text-emerald-600">
                  <Gift className="h-3.5 w-3.5" />
                  -${giftCardTotal.toFixed(2)}
                </span>
                <span className="text-lg font-bold text-slate-900">${chargeBaseAmount.toFixed(2)}</span>
              </div>
            ) : (
              <span className="text-lg font-bold text-slate-900">
                ${(parsedAmount + shipping.shippingTotal).toFixed(2)}
              </span>
            )}
            <Button onClick={handleReview} disabled={productLines.length === 0}>Review Charge</Button>
          </div>
        ) : step === 'review' ? (
          <>
            <Button variant="secondary" onClick={() => setStep('form')} disabled={isPending}>Back</Button>
            <Button onClick={() => chargeMutation.mutate()} loading={isPending}>
              Save Payment
            </Button>
          </>
        ) : step === 'processing' ? null : (
          <div className="flex w-full items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              {savedPaymentId ? (
                <>
                  <Button variant="secondary" onClick={() => setFollowUp('details')}>
                    View Payment Details
                  </Button>
                  {/* Receipts are only offered for a captured payment — a
                      declined charge is voided server-side, so emailing the
                      patient a receipt for it would be wrong. */}
                  {chargeResult === 'success' ? (
                    <Button variant="secondary" onClick={() => setFollowUp('receipt')}>
                      Send Receipt
                    </Button>
                  ) : null}
                </>
              ) : null}
            </div>
            <Button onClick={onClose}>Close</Button>
          </div>
        )
      }
    >
      {/* Defense-in-depth alongside the chargeMutation `setError(null)`:
          form-validation errors are only actionable on the form/review
          steps. Hiding the banner on processing/result prevents stale
          errors from leaking onto the final outcome screens even if a
          future code path forgets to clear `error`. */}
      {error && step !== 'processing' && step !== 'result' && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {missingMemberDiscount && step === 'form' && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
          <span>
            Member pricing is unavailable — the Member Price discount has not been set up for this account.
            Please configure it in the <strong>Corporate Dashboard → Payment Discounts</strong> table.
          </span>
        </div>
      )}

      {/* Keep the form mounted (hidden) during the review step rather than
          unmounting it. PaymentProductsSection and ForwardBillingMethodCapture
          own their entered data in internal state; unmounting on Review would
          reset them, so pressing Back returned an empty modal. Hiding preserves
          every product line and payment option until the payment is made. */}
      <div className="space-y-5" hidden={step !== 'form'}>
          {/* Shipping sits with the products, directly under their discount and
              total rows, rather than only in the charge breakdown below. Staff
              read the cost off this block, so a fee that appeared only in the
              footer left the two figures disagreeing with nothing on screen to
              explain the difference. */}
          <div>
            <PaymentProductsSection
              prefillLines={prefillProductLines}
              onChange={setProductLines}
              isMember={resolvedIsMember}
              showAsMember={showAsMember}
            />
            {(shipping.appliedFees.length > 0 || shipping.availableFees.length > 0) && (
              <div className="rounded-b-md border border-t-0 border-slate-200">
                {/* Total is the product base plus shipping — what the card is billed
                    before any gift card or surcharge, which the breakdown below
                    then carries through. */}
                <PaymentShippingSection
                  appliedFees={shipping.appliedFees}
                  shippingTotal={shipping.shippingTotal}
                  availableFees={shipping.availableFees}
                  onRemove={shipping.removeFee}
                  onAdd={shipping.addFee}
                  productsTotal={parsedAmount}
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <MerchantSelector value={merchantId} onChange={handleMerchantChange} preferredFacilityId={externalFacilityId} />
            <PaymentSourceSelector
              patientId={patientId}
              value={paymentToken}
              merchantId={merchantId}
              onChange={handleSourceChange}
            />
          </div>

          {paymentToken === 'new' ? (
            <ForwardBillingMethodCapture
              active
              patientId={patientId}
              merchantId={merchantId}
              onSaved={handleNewSourceSaved}
            />
          ) : null}

      {isTerminalMode && selectedTerminal ? (
        <TerminalCheckoutPanel
          terminal={selectedTerminal}
          patientId={patientId}
          // Legacy parity: payment-details-charge-gateway.php:1501 and
          // payments-test.js:2278-2322 charge the gift-card / credit-adjusted
          // net amount on the device, not the gross. Sending gross when gift
          // cards apply causes the customer to be charged more on the reader
          // than the ledger records. Shipping is included for the same reason in
          // reverse: it is part of what the payment records, so the device has to
          // capture it or the ledger would exceed what was taken.
          amount={chargeBaseAmount}
          merchantId={merchantId}
          // Always Checkout on this surface. Legacy
          // payment-details-charge-gateway.php has no tokenize branch — the
          // Payments screen is "capture funds now" only. Demoting to
          // tokenize when amount is empty would change semantics; instead,
          // TerminalCheckoutPanel.run() rejects amount <= 0 with a clean
          // inline error, matching legacy's empty-amount behavior.
          mode="checkout"
              onSuccess={handleTerminalResult}
              onReset={handleTerminalReset}
            />
          ) : null}

          <div className="grid grid-cols-2 gap-4">
            <label className="space-y-1 text-sm text-slate-700">
              <span className="font-medium">Payment Type</span>
              <select
                value={payType}
                onChange={(e) => setPayType(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {PAY_TYPE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </label>
            <label className="space-y-1 text-sm text-slate-700">
              <span className="font-medium">
                Amount
                {amountFromProducts ? (
                  <span className="ml-2 text-xs text-slate-500 font-normal">(from products)</span>
                ) : null}
              </span>
              <input
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '' || /^\d*\.?\d{0,2}$/.test(v)) setAmount(v);
                }}
                disabled={amountFromProducts}
                className="w-full rounded-md border border-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-slate-100"
              />
            </label>
          </div>

          <label className="space-y-1 text-sm text-slate-700">
            <span className="font-medium">Notes</span>
            <textarea
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              rows={2}
              className="w-full rounded-md border border-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </label>

          <div className="border-t border-slate-200 pt-4 space-y-4">
            <GiftCardSelector
              appliedCards={giftCards}
              onChange={setGiftCards}
              // Shipping-inclusive: the server folds shipping in BEFORE deducting
              // gift cards, so a card must be allowed to pay for shipping too.
              maxAmount={Math.max(0, parsedAmount + shipping.shippingTotal)}
              patientId={patientId}
            />
          </div>

          {(parsedAmount > 0 || shipping.shippingTotal > 0) && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm space-y-1">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span>${parsedAmount.toFixed(2)}</span>
              </div>
              {/* A summary line only — the fees themselves are listed with the
                  products above. It stays here because this column has to add up:
                  shipping is listed BEFORE the gift-card deduction because that is
                  the order the server applies them in, a gift card paying down the
                  shipping-inclusive total. Listing it after would make the column
                  fail to add up whenever a card exceeds the product subtotal. */}
              {shipping.shippingTotal > 0 && (
                <div className="flex justify-between">
                  <span>Shipping:</span>
                  <span>+${shipping.shippingTotal.toFixed(2)}</span>
                </div>
              )}
              {giftCardTotal > 0 && (
                <div className="flex justify-between text-green-700">
                  <span>Gift Cards:</span>
                  <span>-${giftCardTotal.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between font-semibold border-t border-slate-300 pt-1">
                <span>Net Charge:</span>
                <span>${chargeBaseAmount.toFixed(2)}</span>
              </div>
              {surchargeAmount > 0 && (
                <div className="flex justify-between text-slate-700">
                  <span>Card Surcharge:</span>
                  <span>+${surchargeAmount.toFixed(2)}</span>
                </div>
              )}
              {(surchargeAmount > 0 || shipping.shippingTotal > 0) && (
                <div className="flex justify-between font-bold border-t border-slate-300 pt-1">
                  <span>Total to Charge:</span>
                  <span>${totalToCharge.toFixed(2)}</span>
                </div>
              )}
            </div>
          )}
        </div>

      {step === 'review' && (
        <div className="space-y-4">
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm space-y-2">
            <h3 className="font-semibold text-blue-900">Review Charge</h3>
            {isTerminalMode && terminalResult?.mode === 'checkout' ? (
              <p className="rounded border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800">
                {terminalResult.data.masked_pan
                  ? `Card approved (****${terminalResult.data.masked_pan}). Saving the payment…`
                  : 'Card approved on terminal. Saving the payment…'}
              </p>
            ) : null}
            <div className="grid grid-cols-2 gap-2 text-blue-800">
              <span>Patient:</span>
              <span className="font-medium">{patientName ?? `Patient #${patientId}`}</span>
              <span>Payment Method:</span>
              <span className="font-medium">
                {isTerminalMode && selectedTerminal
                  ? `Terminal · ${selectedTerminal.title}`
                  : selectedMethod?.title ?? 'Forward'}
              </span>
              <span>Payment Type:</span>
              <span className="font-medium">{payType}</span>
              <span>Amount:</span>
              <span className="font-medium">${parsedAmount.toFixed(2)}</span>
              {shipping.appliedFees.map((fee) => (
                <Fragment key={fee.id}>
                  <span>Shipping{fee.name ? ` (${fee.name})` : ''}:</span>
                  <span className="font-medium">+${fee.amount.toFixed(2)}</span>
                </Fragment>
              ))}
              {giftCardTotal > 0 && (
                <>
                  <span>Gift Card Deduction:</span>
                  <span className="font-medium">-${giftCardTotal.toFixed(2)}</span>
                </>
              )}
              <span className="font-bold">Net Charge:</span>
              <span className="font-bold">${chargeBaseAmount.toFixed(2)}</span>
              {surchargeAmount > 0 && (
                <>
                  <span>Card Surcharge:</span>
                  <span className="font-medium">+${surchargeAmount.toFixed(2)}</span>
                </>
              )}
              {(surchargeAmount > 0 || shipping.shippingTotal > 0) && (
                <>
                  <span className="font-bold">Total to Charge:</span>
                  <span className="font-bold">${totalToCharge.toFixed(2)}</span>
                </>
              )}
              <span>Date:</span>
              <span className="font-medium">{new Date().toLocaleDateString()}</span>
              <span>Merchant:</span>
              <span className="font-medium">{merchant?.friendlyTitle ?? '—'}</span>
              <span>Facility:</span>
              <span className="font-medium">{facilityId || '—'}</span>
            </div>
          </div>
        </div>
      )}

      {step === 'processing' && (
        <div className="flex flex-col items-center justify-center py-12 space-y-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600" />
          <p className="text-sm text-slate-600">Saving payment details...</p>
        </div>
      )}

      {step === 'result' && (
        <div className="space-y-3">
          <div className={`rounded-md px-4 py-3 text-sm ${
            chargeResult === 'success'
              ? 'border border-green-200 bg-green-50 text-green-800'
              : 'border border-red-200 bg-red-50 text-red-800'
          }`}>
            {chargeResult === 'success' ? (
              <>
                <p className="font-semibold">Payment saved successfully.</p>
                {intent?.id && <p className="mt-1 text-xs">Pending gateway transaction: {intent.id}</p>}
              </>
            ) : (
              <>
                <p className="font-semibold">Payment was declined.</p>
                {declineMessage ? (
                  <p className="mt-1 text-xs">{declineMessage}</p>
                ) : (
                  <p className="mt-1 text-xs">Please try again with a different method.</p>
                )}
              </>
            )}
          </div>
          {saveWarning ? (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <p className="font-semibold">Payment method not saved.</p>
              <p className="mt-1 text-xs">{saveWarning}</p>
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
